import React, { useMemo, useState } from 'react';
import { cx } from '../ui';

// Halvard (fictional maker) brand used on everything the owner and installer see.
export const KD = { ink: '#15302D', paper: '#F2F4F1', accent: '#2E6F6A', soft: '#E3EEEC', line: '#DCE3E0', muted: '#5E6B68' };

// ---------- Hotspot: a pulsing ring over a target, with a label and a hit area ----------
export function Hot({
  active,
  label,
  onAct,
  children,
  className,
  radius = 12,
  tip = 'top',
}: {
  active: boolean;
  label: string;
  onAct: () => void;
  children: React.ReactNode;
  className?: string;
  radius?: number;
  tip?: 'top' | 'bottom' | 'left' | 'right' | 'none';
}) {
  return (
    <div className={cx('relative', className)}>
      {children}
      {active && (
        <>
          <span className="bmt-ring" style={{ borderRadius: radius + 5 }} aria-hidden="true" />
          <button type="button" className="bmt-hit" style={{ borderRadius: radius }} onClick={onAct} aria-label={label} />
          {tip !== 'none' && (
            <span className={`bmt-tip bmt-tip-${tip}`} aria-hidden="true">
              {label}
            </span>
          )}
        </>
      )}
    </div>
  );
}

// ---------- Device frames ----------
export function PhoneFrame({ children, scale = 1 }: { children: React.ReactNode; scale?: number }) {
  const W = 290;
  const H = 600;
  return (
    <div style={{ width: W * scale, height: H * scale }} className="shrink-0">
      <div
        style={{ width: W, height: H, transform: scale === 1 ? undefined : `scale(${scale})`, transformOrigin: 'top left', borderRadius: 46, padding: 9, background: '#181343', boxShadow: '0 40px 80px -36px rgba(24,19,67,.55), inset 0 0 0 1.5px rgba(255,255,255,.08)' }}
      >
        <div className="relative h-full w-full overflow-hidden" style={{ borderRadius: 37, background: KD.paper }}>
          <div className="absolute left-1/2 top-[9px] z-30 h-[22px] w-[82px] -translate-x-1/2 rounded-full bg-[#181343]" />
          {children}
        </div>
      </div>
    </div>
  );
}

export function StatusBar({ dark = false }: { dark?: boolean }) {
  const c = dark ? '#fff' : KD.ink;
  return (
    <div className="flex h-[40px] items-center justify-between px-6 pt-1 text-[12px] font-semibold" style={{ color: c }}>
      <span>9:41</span>
      <span className="flex items-center gap-1.5" aria-hidden="true">
        <svg width="16" height="10" viewBox="0 0 16 10"><path d="M1 9h2V6H1zM5 9h2V4H5zM9 9h2V2H9zM13 9h2V0h-2z" fill={c} /></svg>
        <svg width="22" height="11" viewBox="0 0 22 11"><rect x=".5" y=".5" width="18" height="10" rx="3" fill="none" stroke={c} opacity=".5" /><rect x="2" y="2" width="13" height="7" rx="1.5" fill={c} /><rect x="19.5" y="3.5" width="1.5" height="4" rx=".7" fill={c} opacity=".5" /></svg>
      </span>
    </div>
  );
}

// ---------- Halvard (fictional maker): marks, the branded code, the rating plate ----------
export function KMono({ size = 20, bg = KD.accent, fg = '#fff', radius = 5 }: { size?: number; bg?: string; fg?: string; radius?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" aria-hidden="true">
      <rect width="20" height="20" rx={radius} fill={bg} />
      <path d="M7.2 4.8v10.4M7.4 10.6 13.2 4.8M9.6 8.6l4 6.6" stroke={fg} strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </svg>
  );
}

export function HalvardMark({ size = 18, light = false }: { size?: number; light?: boolean }) {
  return (
    <span className="inline-flex items-center gap-1.5" style={{ color: light ? '#fff' : KD.ink }}>
      <KMono size={size} bg={light ? '#fff' : KD.accent} fg={light ? KD.accent : '#fff'} />
      <span style={{ fontWeight: 700, letterSpacing: '-0.01em', fontSize: size * 0.9, lineHeight: 1 }}>halvard</span>
    </span>
  );
}

// Halvard's code: dot modules, rounded finder eyes, the K monogram in the centre. Decorative, not scannable.
export function HalvardCode({ size = 64, seed = 4182, fg = KD.accent, bg = '#fff' }: { size?: number; seed?: number; fg?: string; bg?: string }) {
  const N = 25;
  const dots = useMemo(() => {
    let s = seed;
    const rnd = () => ((s = (s * 9301 + 49297) % 233280) / 233280);
    const out: [number, number][] = [];
    const nearFinder = (r: number, c: number) =>
      [[0, 0], [0, N - 7], [N - 7, 0]].some(([r0, c0]) => r >= r0 - 1 && r <= r0 + 7 && c >= c0 - 1 && c <= c0 + 7);
    const inLogo = (r: number, c: number) => r >= 9 && r <= 15 && c >= 9 && c <= 15;
    for (let r = 0; r < N; r++)
      for (let c = 0; c < N; c++) {
        const v = rnd();
        if (nearFinder(r, c) || inLogo(r, c)) continue;
        if (v > 0.5) out.push([r, c]);
      }
    return out;
  }, [seed]);
  const eye = (x: number, y: number) => (
    <g key={`${x}-${y}`}>
      <rect x={x + 0.5} y={y + 0.5} width="6" height="6" rx="2.1" fill="none" stroke={fg} strokeWidth="1" />
      <rect x={x + 2} y={y + 2} width="3" height="3" rx="1" fill={fg} />
    </g>
  );
  return (
    <svg width={size} height={size} viewBox={`-1.5 -1.5 ${N + 3} ${N + 3}`} aria-hidden="true">
      <rect x="-1.5" y="-1.5" width={N + 3} height={N + 3} rx="3" fill={bg} />
      {dots.map(([r, c]) => (
        <circle key={`${r}-${c}`} cx={c + 0.5} cy={r + 0.5} r="0.43" fill={fg} />
      ))}
      {eye(0, 0)}
      {eye(N - 7, 0)}
      {eye(0, N - 7)}
      <g transform="translate(9.6 9.6) scale(0.29)">
        <rect width="20" height="20" rx="5" fill={fg} />
        <path d="M7.2 4.8v10.4M7.4 10.6 13.2 4.8M9.6 8.6l4 6.6" stroke={bg} strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      </g>
    </svg>
  );
}

// Halvard's rating plate on the side of the unit, with the code designed into it.
export function RatingPlate({ className, qr = 62, compact = false, responsive = false }: { className?: string; qr?: number; compact?: boolean; responsive?: boolean }) {
  return (
    <div
      className={cx('relative overflow-hidden rounded-[9px] text-left', className)}
      style={{ background: '#EEF0EE', boxShadow: 'inset 0 1px 0 rgba(255,255,255,.9), inset 0 -1px 0 rgba(0,0,0,.12), 0 10px 22px -10px rgba(0,0,0,.45)' }}
    >
      <div className="flex items-center justify-between px-2.5 py-[5px]" style={{ background: KD.accent }}>
        <HalvardMark size={12} light />
        <span className={cx('font-mono text-[8.5px] font-semibold tracking-[0.08em] text-white/85', responsive && 'hidden sm:inline')}>AIR SOURCE HEAT PUMP</span>
      </div>
      <div className="flex items-center gap-2.5 px-2.5 pb-2 pt-2">
        <div className="shrink-0 rounded-[7px] bg-white p-[3px]" style={{ boxShadow: `inset 0 0 0 1px ${KD.line}` }}>
          <HalvardCode size={qr} />
        </div>
        <div className="min-w-0 leading-tight" style={{ color: KD.ink }}>
          <div className="text-[11.5px] font-bold [word-spacing:0.12em]">Meet your Halvard AW-9</div>
          <div className="mt-0.5 text-[8.5px]" style={{ color: KD.muted }}>Point your camera here</div>
          {!compact && (
            <dl className={cx('mt-1.5 grid-cols-[auto_1fr] gap-x-2 font-mono text-[8px] leading-[1.45]', responsive ? 'hidden sm:grid' : 'grid')}>
              <dt style={{ color: KD.muted }}>MODEL</dt>
              <dd className="font-semibold">AW-9</dd>
              <dt style={{ color: KD.muted }}>SERIAL</dt>
              <dd className="font-semibold">KAW9-26-04182</dd>
              <dt style={{ color: KD.muted }}>SUPPLY</dt>
              <dd className="font-semibold">230 V ~ 50 Hz</dd>
            </dl>
          )}
        </div>
      </div>
      {['left-1 top-[26px]', 'right-1 top-[26px]', 'left-1 bottom-1', 'right-1 bottom-1'].map((p) => (
        <span key={p} className={cx('absolute h-[4px] w-[4px] rounded-full', p)} style={{ background: '#B9BFBC', boxShadow: 'inset 0 1px 1px rgba(0,0,0,.3)' }} />
      ))}
    </div>
  );
}

// ---------- Photographs, with a fallback while generated shots are pending ----------
export function Photo({
  src,
  fallback,
  alt,
  className,
  style,
  fallbackStyle,
  width,
  height,
  eager = false,
}: {
  src: string;
  fallback: string;
  alt: string;
  className?: string;
  style?: React.CSSProperties;
  fallbackStyle?: React.CSSProperties;
  width?: number;
  height?: number;
  eager?: boolean;
}) {
  const [failed, setFailed] = useState(false);
  return (
    <img
      src={failed ? fallback : src}
      alt={alt}
      width={width}
      height={height}
      loading={eager ? 'eager' : 'lazy'}
      decoding="async"
      onError={() => !failed && setFailed(true)}
      className={cx('object-cover', className)}
      style={{ ...style, ...(failed ? fallbackStyle : null) }}
    />
  );
}

// The unit itself: catalogue shot, or a crop of the installation photo onto the unit.
export function UnitPhoto({ className, style, alt = 'Halvard AW-9 air source heat pump' }: { className?: string; style?: React.CSSProperties; alt?: string }) {
  return (
    <Photo
      src="/images/v2/gen/unit-heatpump.jpg"
      fallback="/images/v2/gen/heatpump-commission-b-thumb.jpg"
      alt={alt}
      className={className}
      style={style}
      fallbackStyle={{ objectPosition: '96% 62%' }}
    />
  );
}

// ---------- Icons (1.8px line) ----------
const I = ({ d, size = 16, className }: { d: string; size?: number; className?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    <path d={d} />
  </svg>
);
export const Icon = {
  book: (p: { size?: number }) => <I {...p} d="M4 5a2 2 0 0 1 2-2h13v16H6a2 2 0 0 0-2 2V5zM4 19a2 2 0 0 1 2-2h13" />,
  cog: (p: { size?: number }) => <I {...p} d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />,
  chat: (p: { size?: number }) => <I {...p} d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.6A8 8 0 1 1 21 12z" />,
  wrench: (p: { size?: number }) => <I {...p} d="M14.7 6.3a4 4 0 0 0 5 5L22 14l-8 8-2.3-2.3a4 4 0 0 0-5-5L3 11l3-3 3.7 3.7 2.6-2.6L8.6 5.4l3-3 3.1 3.9z" />,
  alert: (p: { size?: number }) => <I {...p} d="M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />,
  check: (p: { size?: number }) => <I {...p} d="M5 12.5l4.5 4.5L19 7.5" />,
  chev: (p: { size?: number }) => <I {...p} d="M9 6l6 6-6 6" />,
  doc: (p: { size?: number }) => <I {...p} d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9l-6-6zM14 3v6h6M8 13h8M8 17h5" />,
  camera: (p: { size?: number }) => <I {...p} d="M3 8a2 2 0 0 1 2-2h2l2-2h6l2 2h2a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8zM12 17a4 4 0 1 0 0-8 4 4 0 0 0 0 8z" />,
  user: (p: { size?: number }) => <I {...p} d="M20 21a8 8 0 1 0-16 0M12 13a4 4 0 1 0 0-8 4 4 0 0 0 0 8z" />,
  swap: (p: { size?: number }) => <I {...p} d="M7 4 3 8l4 4M3 8h14M17 20l4-4-4-4M21 16H7" />,
  bell: (p: { size?: number }) => <I {...p} d="M6 8a6 6 0 1 1 12 0c0 7 3 9 3 9H3s3-2 3-9M10.3 21a1.9 1.9 0 0 0 3.4 0" />,
  lock: (p: { size?: number }) => <I {...p} d="M5 11h14v10H5zM8 11V7a4 4 0 1 1 8 0v4" />,
  shield: (p: { size?: number }) => <I {...p} d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10zM9 12l2 2 4-4" />,
  search: (p: { size?: number }) => <I {...p} d="M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14zM20 20l-4-4" />,
  link: (p: { size?: number }) => <I {...p} d="M10 14a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1M14 10a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1" />,
  mail: (p: { size?: number }) => <I {...p} d="M3 5h18v14H3zM3 6l9 7 9-7" />,
  x: (p: { size?: number }) => <I {...p} d="M6 6l12 12M18 6 6 18" />,
  arrowL: (p: { size?: number }) => <I {...p} d="M19 12H5M11 6l-6 6 6 6" />,
  arrowR: (p: { size?: number }) => <I {...p} d="M5 12h14M13 6l6 6-6 6" />,
  phone: (p: { size?: number }) => <I {...p} d="M7 2h10a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1zM11 18h2" />,
  window: (p: { size?: number }) => <I {...p} d="M3 4h18v16H3zM3 9h18" />,
  fan: (p: { size?: number }) => <I {...p} d="M12 12m-1.5 0a1.5 1.5 0 1 0 3 0 1.5 1.5 0 1 0-3 0M12 10.5C11 7 12 4 15 4s3 4-1.6 6.3M13.5 12c3.5-1 6.5 0 6.5 3s-4 3-6.3-1.6M12 13.5c1 3.5 0 6.5-3 6.5s-3-4 1.6-6.3M10.5 12C7 13 4 12 4 9s4-3 6.3 1.6" />,
  home: (p: { size?: number }) => <I {...p} d="M3 11l9-7 9 7M5 9.5V20h14V9.5M10 20v-6h4v6" />,
  calendar: (p: { size?: number }) => <I {...p} d="M4 6h16v14H4zM4 10h16M8 3v4M16 3v4" />,
  recycle: (p: { size?: number }) => <I {...p} d="M7 19H4.5a1.5 1.5 0 0 1-1.3-2.2L5.5 13M11 19h8.5a1.5 1.5 0 0 0 1.3-2.2L19 13.5M9 16l2 3-2 3M8.5 6.5l1.8-3a1.5 1.5 0 0 1 2.6 0l4.6 7.8M5 10.5l3.5-2 1 3.6M14.5 11.4l3.5 2 .9-3.8" />,
  thermo: (p: { size?: number }) => <I {...p} d="M14 14.8V5a2 2 0 1 0-4 0v9.8a4 4 0 1 0 4 0zM12 17v-5" />,
};

// A hand-drawn-looking signature line. draw = animate the stroke in.
export function Signature({ variant = 0, draw = false }: { variant?: 0 | 1; draw?: boolean }) {
  const d =
    variant === 0
      ? 'M4 26c6-14 10-20 13-16s-6 16-2 15 9-18 13-16-3 14 1 13 6-9 9-8 1 6 4 5 6-6 9-6 4 3 9 1'
      : 'M4 24c4-10 8-17 11-13s-4 14 0 13 7-12 10-11 0 9 3 9 5-8 8-8 2 7 6 6c3-1 5-6 8-6s3 4 7 3';
  return (
    <svg viewBox="0 0 80 34" className="h-[34px] w-full" aria-hidden="true">
      <path d={d} fill="none" stroke="#1E2A6B" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={draw ? 'bmt-sigdraw' : undefined} pathLength={1} strokeDasharray={draw ? 1 : undefined} />
    </svg>
  );
}
